import { Link, useLocation } from "react-router-dom";
import searchConsoleIndexTargets from "../data/searchConsoleIndexTargets.json";
import { readerGuideByPath } from "../data/readerGuides20261006";
import { aiArticles } from "../data/aiArticles";
import { newsArticles } from "../data/newsArticles";
import { xRoboticsArticles } from "../data/xRoboticsArticles";
import { getLocalizedCertificationArticle } from "../data/certificationArticles2026";
import { getLocalizedAIArticle } from "../data/localizedAIArticles2026";
import { parseLocalizedPriorityPath } from "../data/localizedPriorityPages";
import {
  getSearchConsoleHubPage,
  normalizeSearchConsolePath,
  searchConsoleEditionHubs,
  searchConsoleSectionCopy,
} from "../data/searchConsoleIndexHubs";

const localizedLocales = new Set(["de", "fr", "ja", "zh-cn"]);
const articleByPath = new Map(
  [...aiArticles, ...newsArticles, ...xRoboticsArticles]
    .filter((item) => item?.path)
    .map((item) => [normalizeSearchConsolePath(item.path), item]),
);
const knownTerms = {
  ai: "AI", amr: "AMR", amrs: "AMRs", chatgpt: "ChatGPT",
  claude: "Claude", deepseek: "DeepSeek", fifa: "FIFA", grok: "Grok",
  humanoid: "Humanoid", ia: "IA", robot: "Robot", robotics: "Robotics",
  unitree: "Unitree", ubtech: "UBTECH", xiaomi: "Xiaomi",
};
function localeFromPath(path) {
  const first = normalizeSearchConsolePath(path).split("/").filter(Boolean)[0];
  return localizedLocales.has(first) ? first : "en";
}
function humanizePath(path) {
  const segment = decodeURIComponent(normalizeSearchConsolePath(path).split("/").filter(Boolean).at(-1) || "page");
  return segment.split("-").filter(Boolean)
    .map((word) => knownTerms[word.toLowerCase()] || `${word.charAt(0).toUpperCase()}${word.slice(1)}`).join(" ");
}
function resolveEntry(path) {
  const normalized = normalizeSearchConsolePath(path);
  const hub = getSearchConsoleHubPage(normalized);
  if (hub) return { title: hub.heading, description: hub.description };
  const localizedPriority = parseLocalizedPriorityPath(normalized);
  if (localizedPriority && localizedPriority.locale !== "en") {
    const locale = localizedPriority.locale;
    return { title: localizedPriority.page.title[locale], description: localizedPriority.page.description[locale] };
  }
  const segments = normalized.split("/").filter(Boolean);
  const locale = localizedLocales.has(segments[0]) ? segments[0] : "en";
  if (locale !== "en" && segments[1] === "ai" && segments[2]) {
    const localizedArticle = getLocalizedAIArticle(locale, segments[2]) || getLocalizedCertificationArticle(locale, segments[2]);
    if (localizedArticle) return { title: localizedArticle.title, description: localizedArticle.description || localizedArticle.heroSubtitle };
  }
  const article = articleByPath.get(normalized);
  if (article) return { title: article.title, description: article.description || article.heroSubtitle || article.trendReason };
  return { title: humanizePath(normalized), description: "" };
}
export default function SearchConsoleLinkSection() {
  const location = useLocation();
  const currentPath = normalizeSearchConsolePath(location.pathname);
  // Revised guides provide their own maintained navigation inside the article.
  if (readerGuideByPath[currentPath]) return null;
  const editionLinks = searchConsoleEditionHubs[currentPath];
  const targetLinks = searchConsoleIndexTargets[currentPath];
  const links = editionLinks || targetLinks;
  if (!Array.isArray(links) || links.length === 0) return null;
  const locale = localeFromPath(currentPath);
  const copy = searchConsoleSectionCopy[locale] || searchConsoleSectionCopy.en;
  const hub = getSearchConsoleHubPage(currentPath);
  const edition = Boolean(editionLinks);
  const title = edition ? copy.editionTitle : hub?.directoryHeading || copy.title;
  const description = edition ? copy.editionDescription : copy.description;
  const eyebrow = edition ? copy.editionEyebrow : copy.eyebrow;
  return (
    <section data-search-console-links={currentPath} className="border-y border-neutral-200 bg-neutral-50" lang={locale === "zh-cn" ? "zh-CN" : locale}>
      <div className="site-shell py-12 sm:py-16">
        <div className="flex flex-col gap-4 border-b border-neutral-200 pb-7 md:flex-row md:items-end md:justify-between">
          <div className="max-w-4xl">
            <p className="eyebrow-chip">{eyebrow}</p>
            <h2 className="mt-4 text-3xl font-black tracking-tight text-neutral-950 sm:text-4xl">{title}</h2>
            <p className="mt-4 text-base leading-8 text-neutral-700">{description}</p>
          </div>
          <p className="w-fit rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-black text-neutral-700">
            {links.length} {locale === "fr" ? "pages" : locale === "de" ? "Seiten" : locale === "ja" ? "ページ" : locale === "zh-cn" ? "个页面" : "pages"}
          </p>
        </div>
        <div className="mt-7 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {links.map((path) => {
            const entry = resolveEntry(path);
            return (
              <Link key={path} to={path} className="group rounded-[1.5rem] border border-neutral-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-md">
                <p className="text-xs font-black uppercase tracking-[0.16em] text-blue-700">{copy.open}</p>
                <h3 className="mt-3 text-lg font-black leading-7 text-neutral-950 group-hover:text-blue-700">{entry.title}</h3>
                {entry.description && <p className="mt-3 line-clamp-3 text-sm leading-7 text-neutral-600">{entry.description}</p>}
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
